import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Plus } from 'lucide-react';
import { buildBoardSearchFilterOptions } from './buildBoardSearchFilterOptions';
import { organizeFilterChipSections } from './filterChipLayout';
import { BoardSearchFilterPopover } from './BoardSearchFilterPopover';
import { BoardSearchFiltersScope } from './BoardSearchFiltersScope';
import { BOARD_FILTER_SCOPE } from './filterScopeUtils';
import useCutoverActive, { defeitoTerm } from '../../pg-native/useCutoverActive';

function FilterChip({ chip, isActive, chipRefs, onSelectFilter }) {
  const hasSelection = chip.count > 0;

  return (
    <div
      className="board-search-filters-chip-anchor"
      ref={(el) => {
        if (el) chipRefs.current[chip.key] = el;
        else delete chipRefs.current[chip.key];
      }}
    >
      <button
        type="button"
        className={[
          'board-search-filters-chip',
          isActive ? 'is-active' : '',
          hasSelection ? 'has-selection' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        aria-label={`Filtrar por ${chip.label}`}
        aria-expanded={isActive}
        onClick={() => onSelectFilter(chip.key)}
      >
        <Plus
          size={11}
          strokeWidth={1.75}
          className="board-search-filters-chip-icon board-search-filters-chip-icon--plus"
          aria-hidden
        />
        <span className="board-search-filters-chip-label">{chip.label}</span>
        {hasSelection ? (
          <span className="board-search-filters-chip-count">{chip.count}</span>
        ) : null}
        <ChevronDown
          size={11}
          strokeWidth={1.75}
          className={[
            'board-search-filters-chip-icon',
            'board-search-filters-chip-icon--chevron',
            isActive ? 'is-open' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          aria-hidden
        />
      </button>
    </div>
  );
}

export function BoardSearchFiltersMenu({
  activeFilterKey,
  onSelectFilter,
  onClose,
  onPanelClose,
  filterScope,
  hasVisibleTechnicianColumns = true,
  onFilterScopeChange,
  selectedColumnFilters,
  selectedTechnicianColumnFilters,
  emAbertoCounts,
  technicianCounts,
  onApplyPanelFilters,
  displayFilters,
  scopedCountsProps,
  previsaoWithCounts,
  futuraOrders,
  retornoCounts,
  panelRemountKey,
}) {
  const chipRefs = useRef({});
  const [anchorRect, setAnchorRect] = useState(null);
  const cutoverActive = useCutoverActive();

  const { filterOptions, defeitoSubOptions } = useMemo(() => {
    const built = buildBoardSearchFilterOptions({
      scope: filterScope,
      selectedColumnFilters,
      selectedTechnicianColumnFilters,
      emAbertoCounts,
      technicianCounts,
    });
    if (!cutoverActive) return built;
    const term = defeitoTerm(true);
    const relabel = (chip) => (chip.key === 'defeitoDescricao' ? { ...chip, label: term } : chip);
    return {
      filterOptions: built.filterOptions.map(relabel),
      defeitoSubOptions: built.defeitoSubOptions.map(relabel),
    };
  }, [
    filterScope,
    selectedColumnFilters,
    selectedTechnicianColumnFilters,
    emAbertoCounts,
    technicianCounts,
    cutoverActive,
  ]);

  const { clearOption, topChips, sections } = useMemo(
    () => organizeFilterChipSections(filterOptions, defeitoSubOptions),
    [filterOptions, defeitoSubOptions]
  );

  useLayoutEffect(() => {
    if (!activeFilterKey) {
      setAnchorRect(null);
      return;
    }
    const el = chipRefs.current[activeFilterKey];
    if (!el) {
      setAnchorRect(null);
      return;
    }
    const next = el.getBoundingClientRect();
    setAnchorRect((prev) => {
      if (
        prev &&
        prev.top === next.top &&
        prev.left === next.left &&
        prev.bottom === next.bottom &&
        prev.right === next.right
      ) {
        return prev;
      }
      return next;
    });
  }, [activeFilterKey]);

  useLayoutEffect(() => {
    if (!activeFilterKey) return undefined;
    const update = () => {
      const el = chipRefs.current[activeFilterKey];
      if (el) setAnchorRect(el.getBoundingClientRect());
    };
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [activeFilterKey]);

  return (
    <>
      <div
        className="board-search-filters-menu"
        role="dialog"
        aria-label="Filtros do board"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="board-search-filters-menu-header" data-tour="bsf-menu-header">
          <span className="board-search-filters-menu-title">Filtros</span>
          <BoardSearchFiltersScope
            value={filterScope}
            onChange={onFilterScopeChange}
            disabledScopes={
              hasVisibleTechnicianColumns
                ? []
                : [BOARD_FILTER_SCOPE.TECNICOS, BOARD_FILTER_SCOPE.AMBOS]
            }
          />
          <button
            type="button"
            className="board-search-filters-menu-close"
            onClick={onClose}
            aria-label="Fechar filtros"
          >
            ×
          </button>
        </div>

        <div
          className="board-search-filters-chips"
          role="group"
          aria-label="Opções de filtro"
          data-tour="bsf-chips"
        >
          <div className="board-search-filters-chips-top">
            {clearOption ? (
              <button
                type="button"
                className="board-search-filters-chip board-search-filters-chip--clear"
                onClick={() => onSelectFilter('clearAll')}
              >
                {clearOption.label}
              </button>
            ) : null}
            {topChips.map((chip) => (
              <FilterChip
                key={chip.key}
                chip={chip}
                isActive={activeFilterKey === chip.key}
                chipRefs={chipRefs}
                onSelectFilter={onSelectFilter}
              />
            ))}
          </div>

          {sections.map((section) => (
            <div key={section.id} className="bsf-chip-section">
              <div className="bsf-chip-section-title">
                <section.Icon size={12} strokeWidth={2} aria-hidden />
                <span>{section.title}</span>
              </div>
              <div className="bsf-chip-section-chips">
                {section.chips.map((chip) => (
                  <FilterChip
                    key={chip.key}
                    chip={chip}
                    isActive={activeFilterKey === chip.key}
                    chipRefs={chipRefs}
                    onSelectFilter={onSelectFilter}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <BoardSearchFilterPopover
        anchorRect={anchorRect}
        filterType={activeFilterKey}
        filterScope={filterScope}
        onClose={onPanelClose}
        onApply={onApplyPanelFilters}
        displayFilters={displayFilters}
        countsProps={scopedCountsProps}
        previsaoWithCounts={previsaoWithCounts}
        futuraOrders={futuraOrders}
        retornoCounts={retornoCounts}
        panelRemountKey={panelRemountKey}
      />
    </>
  );
}
